import { inject, Injectable } from "@angular/core";

import { SessionStorage } from "@dffedb/util";

export const SESSION_REAUTH_STORAGE_KEY = "session.reauth";

export interface SessionReauthCredentials {
    brugerNavn: string;
    hashedKodeord: string;
    /** Tidspunkt (ms) for det oprindelige interaktive login – bruges til absolut maks-sessionslængde. */
    loginTidspunkt: number;
}

/**
 * Holder brugernavn + MD5-hashet kodeord i sessionStorage, så en udløbet appserver-session (cryptId)
 * kan fornyes stille uden at brugeren skal logge ind igen. Ryddes ved logout og når fanen/appen lukkes.
 */
@Injectable({ providedIn: "root" })
export class SessionReauthCredentialsService {
    private readonly sessionStorage = inject(SessionStorage);

    public gem(brugerNavn: string, hashedKodeord: string): void {
        if (!brugerNavn || !hashedKodeord) {
            return;
        }
        const value: SessionReauthCredentials = { brugerNavn, hashedKodeord, loginTidspunkt: Date.now() };
        this.sessionStorage.setItem(SESSION_REAUTH_STORAGE_KEY, btoa(JSON.stringify(value)));
    }

    /** Opdaterer kodeord-hash (fx efter skift kodeord) uden at nulstille loginTidspunkt. */
    public opdaterKodeord(hashedKodeord: string): void {
        const current = this.hent();
        if (!current || !hashedKodeord) {
            return;
        }
        this.sessionStorage.setItem(SESSION_REAUTH_STORAGE_KEY, btoa(JSON.stringify({ ...current, hashedKodeord })));
    }

    public hent(): SessionReauthCredentials | null {
        try {
            const raw = this.sessionStorage.getItem(SESSION_REAUTH_STORAGE_KEY);
            if (!raw) {
                return null;
            }
            const value = JSON.parse(atob(raw)) as SessionReauthCredentials;
            return value?.brugerNavn && value?.hashedKodeord ? value : null;
        } catch {
            return null;
        }
    }

    public clear(): void {
        this.sessionStorage.removeItem(SESSION_REAUTH_STORAGE_KEY);
    }
}
